import { useEffect, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import {
  loadTargets,
  targetLoginError,
  targetSession,
  useTargets,
  type AuthTargetInfo,
} from '../../identity/session';
import {
  buddyFor,
  type FeedbackStats,
  type MonitorAlert,
  type SystemHealth,
  type UsageOverview,
} from '../api/usage';
import { buddyPath } from '../shared';
import type { Issue } from '../../pages/dashboard/shared';

// nyce.buddy per environment: the last 30 days against the 30 before, system health, the feedback triage backlog and
// open monitor alerts — shared by the Buddy group dashboard and the main dashboard.

export interface BuddySummary {
  target: AuthTargetInfo;
  signedIn: boolean;
  /** Why the environment could not be read at all (signed out, buddy3 refused or unreachable). */
  error: string | null;
  /** Last 30 days by organization, `previous` = the 30 days before. */
  usage: UsageOverview | null;
  health: SystemHealth[] | null;
  feedback: FeedbackStats | null;
  alerts: MonitorAlert[] | null;
  /** Parts that could not be read while the rest did. */
  partErrors: string[];
}

async function summarize(t: AuthTargetInfo): Promise<BuddySummary> {
  const base: BuddySummary = {
    target: t,
    signedIn: targetSession(t.id).isSignedIn(),
    error: null,
    usage: null,
    health: null,
    feedback: null,
    alerts: null,
    partErrors: [],
  };
  if (!base.signedIn) return { ...base, error: targetLoginError(t.id) ?? 'not signed in' };
  const api = buddyFor(t.id);
  const partErrors: string[] = [];
  const soft = <T,>(p: Promise<T>, what: string) =>
    p.catch((e: Error) => {
      partErrors.push(`${what}: ${e.message}`);
      return null;
    });
  // Usage first: when buddy3 refuses the token, every other call would too.
  let usage: UsageOverview;
  try {
    usage = await api.usage({ period: '30d', groupBy: 'organization', compare: true });
  } catch (e) {
    return { ...base, error: (e as Error).message };
  }
  const [health, feedback, alerts] = await Promise.all([
    soft(api.health(), 'health'),
    soft(api.feedbackStats(), 'feedback'),
    soft(api.alerts({ status: 'open', limit: 500 }), 'monitor alerts'),
  ]);
  return { ...base, usage, health, feedback, alerts, partErrors };
}

/** What needs a look: systems down, budgets reached or about to be, an error rate above 5 %. */
export function buddyIssues(list: BuddySummary[]): Issue[] {
  const out: Issue[] = [];
  for (const s of list) {
    const env = s.target.label;
    const usage = (path: string) => buddyPath(path, s.target.id);
    if (s.error && s.signedIn)
      out.push({
        key: `buddy:${s.target.id}`,
        status: 'error',
        name: `nyce.buddy ${env}`,
        what: 'environment',
        detail: s.error,
        to: usage('/buddy'),
      });
    for (const h of s.health ?? [])
      if (h.enabled && (h.status === 'down' || h.status === 'degraded'))
        out.push({
          key: `buddy:${s.target.id}:sys:${h.id}`,
          status: h.status === 'down' ? 'down' : 'warn',
          name: h.name || h.id,
          what: `buddy system · ${env}`,
          detail: h.error ?? (h.status === 'down' ? 'not available' : 'degraded'),
          to: usage('/buddy'),
        });
    for (const g of s.usage?.groups ?? []) {
      if (g.isUnlimited || !g.monthlyLimit) continue;
      const used = g.usedThisMonth ?? 0;
      const reached = used >= g.monthlyLimit;
      const projected = g.projectedThisMonth != null && g.projectedThisMonth >= g.monthlyLimit;
      if (reached || projected)
        out.push({
          key: `buddy:${s.target.id}:limit:${g.id}`,
          status: reached ? 'down' : 'warn',
          name: g.name,
          what: `buddy organization · ${env}`,
          detail: reached
            ? `monthly limit reached (${Math.round((used / g.monthlyLimit) * 100)} %): questions are refused`
            : `projected to reach the monthly limit (${Math.round((g.projectedThisMonth! / g.monthlyLimit) * 100)} %)`,
          to: usage(`/buddy/usage?groupBy=organization&drill=organization:${encodeURIComponent(g.id)}`),
        });
    }
    const rate = s.usage?.totals.errorRate;
    if (rate != null && rate > 0.05)
      out.push({
        key: `buddy:${s.target.id}:errors`,
        status: 'warn',
        name: `nyce.buddy ${env}`,
        what: 'error rate',
        detail: `${Math.round(rate * 100)} % of requests failed in the last 30 days`,
        to: usage('/buddy/usage?period=30d'),
      });
  }
  return out;
}

const TTL = 60_000;
let cache: { at: number; key: string; data: Promise<BuddySummary[]> } | null = null;

/** Every environment with a nyce.buddy (callers with ophub:buddy:read), cached for a minute across dashboards. */
export function useBuddySummary() {
  const hub = useHubLogin();
  const enabled = hasScope(hub?.scopes, SCOPES.buddyRead);
  const { signedIn, targets } = useTargets();
  const signature = targets.map((t) => `${t.id}:${signedIn(t.id)}`).join(',');
  const [state, setState] = useState<{ data: BuddySummary[] | null; loading: boolean; error: string | null }>(
    {
      data: null,
      loading: enabled,
      error: null,
    }
  );
  const [round, setRound] = useState(0);
  // The environments first: a signature taken before they load would change once they do and fetch everything twice.
  const [listed, setListed] = useState(false);
  useEffect(() => {
    if (enabled) void loadTargets().then(() => setListed(true));
  }, [enabled]);
  useEffect(() => {
    if (!enabled || !listed) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    if (round > 0 || !cache || cache.key !== signature || Date.now() - cache.at > TTL)
      cache = {
        at: Date.now(),
        key: signature,
        data: loadTargets().then((list) => Promise.all(list.filter((t) => t.buddyUrl).map(summarize))),
      };
    cache.data.then(
      (data) => alive && setState({ data, loading: false, error: null }),
      (e) => alive && setState({ data: null, loading: false, error: `nyce.buddy: ${(e as Error).message}` })
    );
    return () => {
      alive = false;
    };
  }, [enabled, listed, signature, round]);
  return { ...state, enabled, reload: () => setRound((r) => r + 1) };
}

/** Test hook. */
export const resetBuddySummary = () => {
  cache = null;
};
